import React, { ComponentPropsWithRef, forwardRef } from "react";
import styled from "styled-components";

import { ACTIVITY_VARIANTS } from "@adsk/acc-reference-picker/es/plugins/constants";
import { ActivityCard } from "@adsk/alloy-react-activity-log";
import { useLocale } from "@adsk/alloy-react-locale";
import theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import { AssetWithCategoryAndStatus } from "../../models/assets";
import InlineStatusColorIndicator from "../shared/InlineStatusColorIndicator";

const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  overflow: hidden;
`;

const Name = styled.div(theme.typography.heading4);

const Status = styled.div({
  ...theme.typography.labelSmall,
  display: "inline-block",
  verticalAlign: "middle",
  marginTop: "4px",
});

type AssetsActivityCardProps = ComponentPropsWithRef<typeof ActivityCard> & {
  variant?: typeof ACTIVITY_VARIANTS.ADDED | typeof ACTIVITY_VARIANTS.REMOVED;
  entity: AssetWithCategoryAndStatus;
};

const AssetsActivityCard = forwardRef<HTMLDivElement, AssetsActivityCardProps>(
  ({ entity: asset, variant, ...rest }, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

    return (
      <ActivityCard
        {...rest}
        ref={ref}
        title={
          variant === ACTIVITY_VARIANTS.REMOVED
            ? T.translate("ASSET_ACTIVITY_TITLE_REMOVED")
            : T.translate("ASSET_ACTIVITY_TITLE_ADDED")
        }
      >
        <Details>
          <Name>
            <OverflowTooltip>{asset.clientAssetId}</OverflowTooltip>
          </Name>
          {asset.status && (
            <Status>
              <OverflowTooltip>
                <InlineStatusColorIndicator color={asset.status.color} />
                {asset.status.label}
              </OverflowTooltip>
            </Status>
          )}
        </Details>
      </ActivityCard>
    );
  }
);

export default AssetsActivityCard;
